import { Fragment, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Pencil, Trash2, Loader2, ArrowUpToLine, ArrowDownToLine } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { eventsForRow, formatHM, displayOffsetLabel, isFromDal } from "@/lib/scheduleTime";

const ROW_BG_BY_STATUS = {
  passed: "opacity-40",
  departure: "bg-[#CA2047]/15",
  startup: "bg-[#FFFF00]/10",
  soon: "bg-[#289643]/10",
  upcoming: "",
};

// Baris "besok pagi" (jadwal keberangkatan pagi hari berikutnya yang ada di
// bagian bawah PDF). Diberi latar dan aksen berbeda supaya mudah dibedakan.
const NEXT_DAY_ROW_BG = "bg-[#0b2233]/60";

// Baris keberangkatan KELUAR dari DAL depo. Latar dan aksen cyan untuk
// membedakan visual dari arrival ke DAL (merah). Startup T-30 tidak
// berlaku untuk baris ini.
const DAL_DEPARTURE_ROW_BG = "bg-[#22d3ee]/5";

const rowStatus = (row, now, planDate) => {
  const { departureAt, startupAt } = eventsForRow(row, now, planDate);
  const diffToDep = (departureAt - now) / 1000;
  const diffToStartup = (startupAt - now) / 1000;
  const fromDal = isFromDal(row);

  if (diffToDep <= -60) return { key: "passed", label: "Passed" };
  if (diffToDep >= -60 && diffToDep <= 60)
    return { key: "departure", label: "DEPARTURE" };
  // Baris keberangkatan dari DAL depo TIDAK punya startup T-30 event —
  // langsung lewati status "STARTUP T-30" untuk mereka.
  if (!fromDal && diffToStartup >= -60 && diffToStartup <= 60)
    return { key: "startup", label: "STARTUP T-30" };
  if (diffToStartup > 0 && diffToStartup <= 60 * 60)
    return { key: "soon", label: "Segera" };
  return { key: "upcoming", label: "Upcoming" };
};

export default function ScheduleTable({
  rows,
  now,
  loading,
  onEdit,
  onDelete,
  onToggleNextDay,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  planDate,
}) {
  const decorated = useMemo(
    () => {
      const items = rows.map((r) => {
        const { departureAt, startupAt, offsetMinutes, isSpecialOrigin, isNextDay, isFromDal } =
          eventsForRow(r, now, planDate);
        return {
          row: r,
          status: rowStatus(r, now, planDate),
          effectiveStart: formatHM(departureAt),
          startupTime: formatHM(startupAt),
          offsetMinutes,
          offsetLabel: displayOffsetLabel(r),
          isSpecialOrigin,
          isNextDay,
          isFromDal,
        };
      });
      // Today's rows first, then next-morning rows (each group sorted by time).
      items.sort((a, b) => {
        if (a.isNextDay !== b.isNextDay) return a.isNextDay ? 1 : -1;
        return String(a.row.start_time).localeCompare(String(b.row.start_time));
      });
      return items;
    },
    [rows, now, planDate],
  );

  if (loading) {
    return (
      <div className="border border-[#243a52] rounded-xl p-16 flex items-center justify-center bg-[#14283d]">
        <Loader2 className="h-6 w-6 animate-spin text-[#289643]" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div
        className="border border-dashed border-[#243a52] rounded-xl p-14 text-center bg-[#14283d]/40"
        data-testid="empty-schedule"
      >
        <div className="font-display text-lg font-semibold mb-1">
          Belum ada shunting plan
        </div>
        <div className="text-sm text-slate-400">
          Upload PDF shunting plan untuk hari ini atau tambah baris manual.
        </div>
      </div>
    );
  }

  return (
    <div
      className="border border-[#243a52] rounded-xl overflow-hidden bg-[#14283d]"
      data-testid="schedule-table"
    >
      <Table>
        <TableHeader>
          <TableRow className="border-[#243a52] hover:bg-transparent">
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 w-[13%]">
              Startup (T-30)
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 w-[13%]">
              Start Time
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-400">
              Track Origin
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-400">
              Track Destination
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 w-[13%]">
              Operation ID
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 w-[12%]">
              Trainset ID
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 w-[11%]">
              Status
            </TableHead>
            <TableHead className="text-[10px] uppercase tracking-widest text-slate-400 w-[150px] text-right">
              <div className="flex items-center justify-end gap-2">
                {onToggleSelect && (
                  <Checkbox
                    checked={
                      rows.length > 0 && selectedIds?.size === rows.length
                        ? true
                        : selectedIds?.size > 0
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(v) => onToggleSelectAll?.(!!v)}
                    data-testid="select-all"
                    aria-label="Pilih semua"
                    className="border-slate-500 data-[state=checked]:bg-[#22d3ee] data-[state=checked]:border-[#22d3ee] data-[state=indeterminate]:bg-[#22d3ee]/50"
                  />
                )}
                <span>Aksi</span>
              </div>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {decorated.map(
            ({
              row,
              status,
              effectiveStart,
              startupTime,
              offsetLabel,
              isSpecialOrigin,
              isNextDay,
              isFromDal,
            }, i) => {
              const bgClass = ROW_BG_BY_STATUS[status.key] || "";
              // Only surface a badge for special origins (they differ from the
              // default +1m rule that applies to every row).
              const showBadge = isSpecialOrigin && offsetLabel;
              // Insert a visual divider row right before the first next-day
              // entry so the dispatcher instantly sees the day-break.
              const prev = decorated[i - 1];
              const showNextDayDivider =
                isNextDay && (!prev || !prev.isNextDay);
              const nextDayBg = isNextDay ? NEXT_DAY_ROW_BG : "";
              const dalBg = isFromDal ? DAL_DEPARTURE_ROW_BG : "";
              // Different font family / style for next-day rows so they're
              // visually distinct at a glance (serif italic vs. sans display).
              const timeFontClass = isNextDay
                ? "font-serif italic tracking-wide"
                : "font-mono";
              // Warna font untuk start time:
              //   DAL-origin (keluar depo)  → cyan #22d3ee
              //   arrival next-day          → pink #f472b6
              //   arrival today             → red #FF3B30
              const startTimeColor = isFromDal
                ? "text-[#22d3ee]"
                : isNextDay
                  ? "text-[#f472b6]"
                  : "text-[#FF3B30]";
              return (
                <Fragment key={row.id}>
                  {showNextDayDivider && (
                    <TableRow
                      key={`nextday-divider-${row.id}`}
                      className="border-[#243a52] hover:bg-transparent"
                      data-testid="next-day-divider"
                    >
                      <TableCell
                        colSpan={8}
                        className="bg-[#062036] py-2 border-y border-[#0ea5e9]/40"
                      >
                        <div className="flex items-center gap-3 text-[#7dd3fc]">
                          <span className="h-px flex-1 bg-[#0ea5e9]/30" />
                          <span className="font-serif italic text-xs uppercase tracking-[0.28em] font-semibold">
                            Jadwal Keberangkatan · Besok Pagi
                          </span>
                          <span className="h-px flex-1 bg-[#0ea5e9]/30" />
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow
                    key={row.id}
                    className={`border-[#243a52] hover:bg-[#1c3550] transition-colors duration-150 row-enter ${bgClass} ${nextDayBg} ${dalBg} ${selectedIds?.has(row.id) ? "ring-1 ring-inset ring-[#22d3ee]/40 bg-[#22d3ee]/5" : ""}`}
                    style={{ animationDelay: `${i * 20}ms` }}
                    data-testid={`schedule-row-${row.id}`}
                    data-next-day={isNextDay ? "true" : "false"}
                    data-from-dal={isFromDal ? "true" : "false"}
                    data-selected={selectedIds?.has(row.id) ? "true" : "false"}
                  >
                    <TableCell className={`${timeFontClass} text-base font-semibold ${isFromDal ? "text-slate-500" : isNextDay ? "text-[#facc15]/80" : "text-[#FFFF00]"}`}>
                      {isFromDal ? (
                        <span
                          className="font-mono text-slate-500"
                          title="Startup T-30 tidak berlaku untuk keberangkatan dari DAL depo"
                        >
                          —
                        </span>
                      ) : (
                        startupTime
                      )}
                    </TableCell>
                    <TableCell className={`${timeFontClass} text-base font-semibold ${startTimeColor}`}>
                      <div className="flex items-center gap-2">
                        <span>{effectiveStart}</span>
                        {isFromDal && (
                          <span
                            className="text-[9px] font-sans not-italic px-1.5 py-0.5 rounded-full bg-[#22d3ee]/15 text-[#22d3ee] border border-[#22d3ee]/40 tracking-widest uppercase"
                            title="Keberangkatan keluar dari DAL depo"
                          >
                            Pulang
                          </span>
                        )}
                        {isNextDay && (
                          <span
                            className="text-[9px] font-sans not-italic px-1.5 py-0.5 rounded-full bg-[#0ea5e9]/20 text-[#7dd3fc] border border-[#0ea5e9]/40 tracking-widest uppercase"
                            title="Jadwal keberangkatan besok pagi"
                          >
                            Besok
                          </span>
                        )}
                        {showBadge && (
                          <span
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-[#034694] text-white tracking-wider"
                            title={`Origin ${row.track_origin} · net ${offsetLabel} dari PDF (${row.start_time})`}
                          >
                            {offsetLabel}
                          </span>
                        )}
                      </div>
                      {showBadge && (
                        <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                          PDF: {row.start_time}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className={`text-sm ${isFromDal ? "text-[#22d3ee] font-semibold" : isNextDay ? "italic text-slate-300" : ""}`}>{row.track_origin}</TableCell>
                    <TableCell className={`text-sm ${isFromDal ? "text-[#22d3ee]/80" : isNextDay ? "italic text-slate-300" : ""}`}>
                      {row.track_destination}
                    </TableCell>
                    <TableCell className={`text-xs ${isFromDal ? "font-mono text-[#22d3ee]/70" : isNextDay ? "font-serif italic text-slate-400" : "font-mono text-slate-300"}`}>
                      {row.operation_id}
                    </TableCell>
                    <TableCell className={`text-sm font-semibold ${isFromDal ? "font-mono text-[#22d3ee]" : isNextDay ? "font-serif italic text-slate-200" : "font-mono"}`}>
                      {row.trainset_id}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end items-center gap-1">
                        {onToggleNextDay && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => onToggleNextDay(row, !isNextDay)}
                            data-testid={`toggle-nextday-${row.id}`}
                            title={
                              isNextDay
                                ? "Naikkan ke Hari Ini"
                                : "Turunkan ke Besok Pagi"
                            }
                            className={`h-8 w-8 ${
                              isNextDay
                                ? "hover:bg-[#facc15]/20 text-[#facc15]"
                                : "hover:bg-[#0ea5e9]/20 text-[#7dd3fc]"
                            }`}
                          >
                            {isNextDay ? (
                              <ArrowUpToLine className="h-4 w-4" />
                            ) : (
                              <ArrowDownToLine className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onEdit(row)}
                          data-testid={`edit-${row.id}`}
                          className="h-8 w-8 hover:bg-[#243a52]"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => onDelete(row)}
                          data-testid={`delete-${row.id}`}
                          className="h-8 w-8 hover:bg-[#CA2047]/20"
                        >
                          <Trash2 className="h-4 w-4 text-[#CA2047]" />
                        </Button>
                        {onToggleSelect && (
                          <div className="pl-2 ml-1 border-l border-[#243a52]">
                            <Checkbox
                              checked={!!selectedIds?.has(row.id)}
                              onCheckedChange={(v) => onToggleSelect(row.id, !!v)}
                              data-testid={`select-row-${row.id}`}
                              aria-label={`Pilih baris ${row.trainset_id}`}
                              className="border-slate-500 data-[state=checked]:bg-[#22d3ee] data-[state=checked]:border-[#22d3ee]"
                            />
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                </Fragment>
              );
            },
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    passed: "bg-slate-800 text-slate-400 border-slate-700",
    departure: "bg-[#CA2047] text-white border-transparent",
    startup: "bg-[#FFFF00] text-black border-transparent",
    soon: "bg-[#289643] text-white border-transparent",
    upcoming: "bg-transparent text-slate-300 border-[#243a52]",
  };
  return (
    <Badge
      className={`border ${map[status.key]} font-display uppercase text-[10px] tracking-widest`}
    >
      {status.label}
    </Badge>
  );
}
